<script setup>
import { computed, onMounted, ref, onUnmounted, onBeforeMount } from 'vue';
import HighlightsSection from '@/components/configuratorSections/HighlightsSection';
import PowertrainSection from '@/components/configuratorSections/PowertrainSection';
import ExteriorSection from '@/components/configuratorSections/ExteriorSection.vue';
import InteriorSection from '@/components/configuratorSections/InteriorSection';
import BodyBuildSection from '@/components/configuratorSections/BodyBuildSection';
import WheelsSection from '@/components/configuratorSections/WheelsSection';
import ConnectedServicesSection from '@/components/configuratorSections/ConnectedServicesSection';
import RepairMaintenanceSection from '@/components/configuratorSections/RepairMaintenanceSection';

import { useMainStore } from '@/store/index.js';

const store = useMainStore();

const staticImagesBaseUrl = process.env.VUE_APP_DS_STATIC_IMAGES_BASE_URL;

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  offerTemplate: {
    type: String,
    default: 'CH_STANDARD_RIGID',
    required: true,
  },
  specType: {
    type: String,
    default: '',
    required: true,
  },
  isFrameAgreement: {
    type: Boolean,
    default: false,
    required: true,
  },
});

const emit = defineEmits(['select']);

const marketDefaultHeroImageType = ref(store.activeMarket.defaultHeroImageType);

onMounted(() => {
  store.sections = [
    { id: 'highlights', name: 'highlightsHeaderTitle' },
    { id: 'powertrain', name: 'drivelineHeaderTitle' },
    { id: 'exterior', name: 'exteriorHeaderTitle' },
    { id: 'interior', name: 'interiorHeaderTitle' },
    { id: 'bodyBuild', name: 'bodyBuildHeaderTitle' },
    { id: 'wheels', name: 'wheelsHeaderTitle' },
    { id: 'connectedServices', name: 'connectedServicesHeaderTitle' },
    { id: 'repairMaintenance', name: 'repairMaintenanceHeaderTitle' },
  ];
});

onBeforeMount(() => {
  store.activeMarket.defaultHeroImageType = 'front';
});

onUnmounted(() => {
  store.activeMarket.defaultHeroImageType = marketDefaultHeroImageType.value;
});

const options = computed(() => {
  return store.options;
});

const images = computed(() => {
  return store.images;
});

const interiorImages = computed(() => {
  return { interior: images.value.interior, dashboard: images.value.dashboard };
});

const isBev = computed(() => {
  return props.offerTemplate.endsWith('_BEV');
});

const isRigid = computed(() => {
  return props.offerTemplate.endsWith('_RIGID');
});

const imageBodyBuildSection = computed(() => {
  return (store.sectionMetadata?.bodyBuild?.descriptionImage) ? `${staticImagesBaseUrl}/${store.sectionMetadata?.bodyBuild?.descriptionImage}` : null;
});

const chassisHighlights = computed(() => {
  return store.misc?.chassisHighlights;
});
</script>
<template>
  <HighlightsSection
    v-if="options.highlights"
    :items="options.highlights"
  />
  <PowertrainSection
    :items="options.driveline"
    @select="emit('select', $event, 'driveline')"
  />
  <ExteriorSection
    v-if="options.cabColour"
    cab-colour-image="front"
    :cab-colour="options.cabColour"
    :exterior="options.exterior"
    :images="images"
    :show-cab-colour-information="true"
    @select="emit('select', $event, 'exterior')"
  />
  <InteriorSection
    v-if="options.interior"
    :items="options.interior"
    :images="interiorImages"
    @select="emit('select', $event, 'interior')"
  />
  <BodyBuildSection
    v-if="options.bodyBuild"
    :items="options.bodyBuild"
    :images="{ side: imageBodyBuildSection, isSide: images?.side }"
    :offer-template="offerTemplate"
    :spec-type="specType"
    :is-frame-agreement="isFrameAgreement"
    :is-bev="isBev"
    :is-rigid="isRigid"
    :chassis-description-key="chassisHighlights"
    :show-dimensions-subheader="false"
    class="chasis-section"
    @select="emit('select', $event, 'bodyBuild')"
  />
  <WheelsSection
    v-if="options.wheels"
    :items="options.wheels"
    :offer-template="offerTemplate"
    @select="emit('select', $event, 'wheels')"
  />
  <ConnectedServicesSection
    :items="options.connectedServices"
    @select="emit('select', $event, 'connectedServices')"
  />
  <RepairMaintenanceSection
    :items="options.repairMaintenance"
    @select="emit('select', $event, 'repairMaintenance')"
  />
</template>


<style scoped>
.chasis-section >>> .configurator-section__image > img {
  max-width: 70%;
}

@media only screen and (max-width: 1294px) {
  .chasis-section >>> .configurator-section__image > img {
    max-width: 80%;
  }
}

@media only screen and (max-width: 1000px) {
  .chasis-section >>> .configurator-section__image > img {
    max-width: 90%;
  }
}

@media only screen and (max-width: 600px) {
  .chasis-section >>> .configurator-section__image > img {
    max-width: 120%;
  }
}
</style>
